import { Button, Form, Spinner } from "react-bootstrap";
import ButtonOverride from "./buttonoverride";
import styles from "../styles/DocumentUpload.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMultiply, faPlus, faUpload } from "@fortawesome/free-solid-svg-icons";
import { useRef, useState } from "react";
import { faSave } from "@fortawesome/free-regular-svg-icons";
import { useUploadDocuments } from "../new/query";

const DocumentUploader = ({ fileTypes, applicationId, onFeedback }) => {
    const [uploadMode, setUploadMode] = useState(false);
    const [files, setFiles] = useState([]);
    const [fileTypeId, setFileTypeId] = useState(null);
    const inputRef = useRef(null);

    const uploadMutation = useUploadDocuments(
        applicationId,
        () => {
            setFiles([]);
            setUploadMode(false);
            onFeedback?.({
                type: "success",
                message: "Documents uploaded successfully."
            });
        },
        () => {
            onFeedback?.({
                type: "error",
                message: "Unable to upload the selected documents."
            });
        }
    );

    const validateFile = (fileRef, typeId) => {
        const typeRef = fileTypes.find((fileType) => fileType.id === typeId);
        return !!typeRef?.validTypes?.find((validType) => validType.type === fileRef.type);
    };

    const handleFileAddChange = (event) => {
        const selectedFiles = Array.from(event.target.files).map((file) => ({
            file,
            typeID: fileTypeId
        }));

        const updatedFiles = [...files];

        selectedFiles.forEach((newFile) => {
            const existingFileIndex = updatedFiles.findIndex(
                (existingFile) =>
                    existingFile.typeID === newFile.typeID &&
                    existingFile.file.name === newFile.file.name
            );

            if (existingFileIndex !== -1) {
                updatedFiles.splice(existingFileIndex, 1);
            }

            updatedFiles.push(newFile);
        });

        setFiles(updatedFiles);
        inputRef.current.value = "";
    };

    const handleFileAddClick = (typeId) => {
        setFileTypeId(typeId);
        inputRef.current?.click();
    };

    const handleClose = () => {
        setFiles([]);
        setFileTypeId(null);
        setUploadMode(false);
    };

    const removeItem = (file, typeID) => {
        setFiles((prev) =>
            prev.filter((fileObj) => fileObj.typeID !== typeID || fileObj.file.name !== file.name)
        );
    };

    const handleSubmit = () => {
        uploadMutation.mutate({ applicationId, files });
    };

    const validateSubmit = (selectedFiles) => {
        for (let index = 0; index < selectedFiles.length; index += 1) {
            if (!validateFile(selectedFiles[index].file, selectedFiles[index].typeID)) {
                return false;
            }
        }

        return true;
    };

    const filesByTypeID = files.reduce((acc, { file, typeID }) => {
        if (!acc[typeID]) {
            acc[typeID] = [];
        }

        acc[typeID].push(file);
        return acc;
    }, {});

    const hasFiles = files.length > 0;
    const isValidSelection = validateSubmit(files);

    return !uploadMode ? (
        <div className={styles.uploadDockColumn}>
            <div className={styles.uploadDock}>
                <div className={styles.uploadDockInner}>
                    <div className={styles.uploadDockHeader}>
                        <div>
                            <p className={styles.heroEyebrow}>Upload center</p>
                            <h3 className={styles.uploadDockTitle}>Add documents by type</h3>
                            <p className={styles.uploadDockCopy}>
                                Start here to attach files, keep uploads organized, and avoid scrolling through
                                the existing document list first.
                            </p>
                        </div>
                        <div className={styles.uploadDockIcon} aria-hidden="true">
                            <FontAwesomeIcon icon={faUpload} size="lg" />
                        </div>
                    </div>

                    <div className={styles.uploadDockActions}>
                        <Button
                            type="button"
                            variant="success"
                            className={styles.uploadDockButton}
                            onClick={() => setUploadMode(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Add documents
                        </Button>
                    </div>

                    <div className={styles.uploadDockMeta}>
                        <span className={styles.metaChip}>{fileTypes.length} document types</span>
                        <span className={styles.metaChip}>Mobile-friendly upload flow</span>
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <div className={styles.uploadDockColumn}>
            <div className={styles.uploadComposer}>
                <div className={styles.uploadComposerInner}>
                    <div className={styles.uploadComposerHeader}>
                        <div>
                            <p className={styles.heroEyebrow}>Upload center</p>
                            <h3 className={styles.uploadComposerTitle}>Queue files for upload</h3>
                            <p className={styles.uploadComposerCopy}>
                                Add files to the right document type, review them below, then save everything in one
                                pass.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={handleClose}
                            disabled={uploadMutation.isPending}
                        ></button>
                    </div>

                    <div className={styles.uploadTypeList}>
                        {fileTypes.map((item) => (
                            <section key={item.id} className={styles.uploadTypeCard}>
                                <div className={styles.uploadTypeHeader}>
                                    <div>
                                        <h4 className={styles.uploadTypeTitle}>{item.title}</h4>
                                        <p className={styles.uploadTypeDescription}>
                                            {item.description || "Add one or more files for this document type."}
                                        </p>
                                    </div>
                                    <Button
                                        title={`Add ${item.title}`}
                                        aria-label={`Add ${item.title}`}
                                        type="button"
                                        variant="outline-success"
                                        size="sm"
                                        className={styles.addTypeButton}
                                        onClick={() => handleFileAddClick(item.id)}
                                        disabled={uploadMutation.isPending}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                        Add file
                                    </Button>
                                </div>

                                {filesByTypeID[item.id]?.length > 0 ? (
                                    <div className={styles.selectedFileList}>
                                        {filesByTypeID[item.id].map((file) => {
                                            const isInvalid = !validateFile(file, item.id);

                                            return (
                                                <div
                                                    key={`${item.id}-${file.name}`}
                                                    className={`${styles.selectedFileRow} ${isInvalid ? styles.selectedFileRowInvalid : ""}`}
                                                >
                                                    <div className={styles.selectedFileCopy}>
                                                        <p className={styles.selectedFileName}>{file.name}</p>
                                                        <p className={styles.selectedFileMeta}>
                                                            {isInvalid ? "File type does not match this document type." : "Ready to upload"}
                                                        </p>
                                                    </div>
                                                    <ButtonOverride
                                                        type="button"
                                                        className={styles.deleteAddedButton}
                                                        aria-label={`Remove ${file.name}`}
                                                        title={`Remove ${file.name}`}
                                                        onClick={() => removeItem(file, item.id)}
                                                        disabled={uploadMutation.isPending}
                                                    >
                                                        <FontAwesomeIcon icon={faMultiply} size="lg" style={{ color: "red" }} />
                                                    </ButtonOverride>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </section>
                        ))}
                    </div>

                    <Form encType="multipart/form-data">
                        <input
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFileAddChange}
                            ref={inputRef}
                        />
                    </Form>

                    {uploadMutation.isPending ? (
                        <div className={styles.uploadComposerFooter}>
                            <p className={styles.uploadComposerValidation}>Uploading selected documents.</p>
                            <Button type="button" variant="primary" className={styles.uploadSubmitButton} disabled>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Uploading...
                            </Button>
                        </div>
                    ) : hasFiles ? (
                        <div className={styles.uploadComposerFooter}>
                            {!isValidSelection ? (
                                <p className={styles.uploadComposerValidation}>Invalid file types selected.</p>
                            ) : (
                                <p className={styles.selectedFileMeta}>
                                    {files.length} file{files.length === 1 ? "" : "s"} ready to upload.
                                </p>
                            )}
                            <Button
                                type="button"
                                variant="primary"
                                className={styles.uploadSubmitButton}
                                onClick={handleSubmit}
                                disabled={!isValidSelection}
                            >
                                <FontAwesomeIcon icon={faSave} className="me-2" />
                                Save files
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default DocumentUploader;
